import React from 'react';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import type { UsageDevices, UsageSummary, UsageWatchedOrganisation } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import MuiRouterLink from 'c-components/MuiRouterLink';
import { formatNumberWhole } from 'c-lib';
import { SidePanel, SideSection, TimeAgo } from './UsageParts';
import { organisationLink, percentOf, useFormatDuration } from './usageLib';
import { humanise, useActivityLabel } from './activityLabels';

/**
 * The overview's two side columns (board 1): organisations to watch beside the chart, and what got
 * done beside the active users table.
 */

/**
 * How many names each list shows before "and N more". Real periods can be long (on the day tracking
 * starts, everyone is new), and this column sets the height of the chart beside it.
 */
const NEW_PEOPLE_NAMED = 3;
const NEW_ORGANISATIONS_NAMED = 3;
const QUIET_SHOWN = 3;

const Row = ({ children }: { children: React.ReactNode }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 1,
            minWidth: 0,
        }}
    >
        {children}
    </Box>
);

const Count = ({ value, by }: { value: number; by?: string }) => (
    <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
            {formatNumberWhole(value)}
        </Box>
        {by && (
            <Typography component="span" variant="caption" color="text.secondary">
                {' '}
                {by}
            </Typography>
        )}
    </Typography>
);

const OrgLink = ({ org }: { org: UsageWatchedOrganisation }) => (
    <MuiRouterLink
        to={organisationLink(org.type, org.id)}
        variant="body2"
        fontWeight={500}
        underline="hover"
        sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
        {org.name ?? `#${org.id}`}
    </MuiRouterLink>
);

/** Busiest organisations, those gone quiet, and who's new: beside the chart. */
export const OrganisationsToWatch = ({
    watch,
}: {
    watch: UsageSummary['organisations_to_watch'];
}) => {
    const t = useCommonTranslation();
    const formatDuration = useFormatDuration();
    const max = Math.max(1, ...watch.busiest.map(org => org.time_ms));
    const named = watch.new_users
        .slice(0, NEW_PEOPLE_NAMED)
        .map(user => user.name ?? `#${user.id}`);
    const others = watch.new_users_count - named.length;

    return (
        <SidePanel>
            <SideSection
                title={t('Modules.Admin.Usage.busiestOrganisations')}
                note={t('Modules.Admin.Usage.kpi.timeInPortal')}
            >
                {watch.busiest.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {t('Modules.Admin.Usage.noData')}
                    </Typography>
                )}
                {watch.busiest.map(org => {
                    const up = org.time_ms >= org.previous_time_ms;
                    return (
                        <Box
                            key={`${org.type}-${org.id}`}
                            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                        >
                            <Row>
                                <OrgLink org={org} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {formatDuration(org.time_ms)}{' '}
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: up ? 'success.dark' : 'error.main',
                                        }}
                                        title={t(
                                            up
                                                ? 'Modules.Admin.Usage.upOnPrevious'
                                                : 'Modules.Admin.Usage.downOnPrevious',
                                        )}
                                    >
                                        {up ? '▲' : '▼'}
                                    </Box>
                                </Typography>
                            </Row>
                            <Box sx={{ height: 6, bgcolor: 'action.hover', borderRadius: 999 }}>
                                <Box
                                    sx={{
                                        height: 6,
                                        width: `${percentOf(org.time_ms, max)}%`,
                                        bgcolor: 'primary.main',
                                        borderRadius: 999,
                                    }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </SideSection>

            <SideSection
                title={t('Modules.Admin.Usage.goingQuiet')}
                note={t('Modules.Admin.Usage.goingQuietHint')}
                warning
            >
                {watch.quiet.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {t('Modules.Admin.Usage.nobodyQuiet')}
                    </Typography>
                )}
                {watch.quiet.slice(0, QUIET_SHOWN).map(org => (
                    <Row key={`${org.type}-${org.id}`}>
                        <OrgLink org={org} />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {t('Modules.Admin.Usage.lastSeenLabel')}{' '}
                            <TimeAgo date={org.last_active} />
                        </Typography>
                    </Row>
                ))}
            </SideSection>

            <SideSection
                title={t('Modules.Admin.Usage.newThisPeriod')}
                note={t('Modules.Admin.Usage.newSummary', {
                    people: watch.new_users_count,
                    organisations: watch.new_organisations.length,
                })}
            >
                {watch.new_organisations.length > 0 && (
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {watch.new_organisations
                            .slice(0, NEW_ORGANISATIONS_NAMED)
                            .map((org, index) => (
                                <React.Fragment key={`${org.type}-${org.id}`}>
                                    {index > 0 && ', '}
                                    <OrgLink org={org} />
                                </React.Fragment>
                            ))}
                        {watch.new_organisations.length > NEW_ORGANISATIONS_NAMED && (
                            <Typography component="span" variant="body2" color="text.secondary">
                                {' '}
                                {t('Modules.Admin.Usage.andMore', {
                                    count: watch.new_organisations.length - NEW_ORGANISATIONS_NAMED,
                                })}
                            </Typography>
                        )}
                    </Typography>
                )}
                {named.length > 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {others > 0
                            ? t('Modules.Admin.Usage.namesAndMore', {
                                  names: named.join(', '),
                                  count: others,
                              })
                            : named.join(', ')}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {t('Modules.Admin.Usage.nobodyNew')}
                    </Typography>
                )}
            </SideSection>
        </SidePanel>
    );
};

/** One split bar and a line of words: the overview only needs the gist. */
const DevicesLine = ({ devices }: { devices: UsageDevices }) => {
    const t = useCommonTranslation();
    const theme = useTheme();
    const total = devices.types.reduce((sum, type) => sum + type.sessions, 0);
    const shades = [1, 0.55, 0.3, 0.15];
    const types = devices.types.map((type, index) => ({
        key: type.device_type,
        label: t(`Modules.Admin.Usage.deviceTypes.${type.device_type}`),
        pct: percentOf(type.sessions, total),
        colour: alpha(theme.palette.primary.main, shades[Math.min(index, shades.length - 1)]),
    }));
    const usual = devices.browsers[0];
    const words = types.map(type => `${type.label} ${type.pct}%`).join(' · ');

    if (types.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t('Modules.Admin.Usage.noData')}
            </Typography>
        );
    }

    return (
        <>
            <Box
                role="img"
                aria-label={words}
                sx={{
                    display: 'flex',
                    height: 8,
                    borderRadius: 999,
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                }}
            >
                {types.map(type => (
                    <Box key={type.key} sx={{ width: `${type.pct}%`, bgcolor: type.colour }} />
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
                {usual
                    ? t('Modules.Admin.Usage.devicesMostly', {
                          split: words,
                          usual: usual.os
                              ? t('Modules.Admin.Usage.deviceOn', {
                                    browser: usual.browser,
                                    os: usual.os,
                                })
                              : usual.browser,
                      })
                    : words}
            </Typography>
        </>
    );
};

/** What got done, the emails we sent, and devices: beside the active users table. */
export const WhatGotDone = ({ summary }: { summary: UsageSummary }) => {
    const t = useCommonTranslation();
    const activityLabel = useActivityLabel();

    return (
        <SidePanel>
            <SideSection
                title={t('Modules.Admin.Usage.whatPeopleDid')}
                note={t('Modules.Admin.Usage.whatPeopleDidHint')}
            >
                {summary.actions.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {t('Modules.Admin.Usage.noData')}
                    </Typography>
                )}
                {summary.actions.map(action => (
                    <Row key={action.name}>
                        <Typography variant="body2">
                            {activityLabel({ kind: 'event', name: action.name })}
                        </Typography>
                        <Count
                            value={action.count}
                            by={t('Modules.Admin.Usage.byPeople', { count: action.people })}
                        />
                    </Row>
                ))}
            </SideSection>
            <SideSection
                title={t('Modules.Admin.Usage.emailsSent')}
                note={t('Modules.Admin.Usage.emailsSentSummary', {
                    sent: formatNumberWhole(summary.emails.sent),
                    count: summary.emails.people,
                })}
            >
                {summary.emails.by_type.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {t('Modules.Admin.Usage.noEmails')}
                    </Typography>
                )}
                {summary.emails.by_type.map(email => (
                    <Row key={email.name}>
                        <Typography variant="body2">{humanise(email.name)}</Typography>
                        <Count value={email.count} />
                    </Row>
                ))}
            </SideSection>
            <SideSection
                title={t('Modules.Admin.Usage.devicesTitle')}
                note={t('Modules.Admin.Usage.devicesHint')}
            >
                <DevicesLine devices={summary.devices} />
            </SideSection>
        </SidePanel>
    );
};
